import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Select as ChakraSelect,
  SimpleGrid,
  FormErrorMessage,
  useToast,
  Skeleton,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { Select } from "chakra-react-select";
import { FetchCountryOptions } from "views/admin/campaign_create/action";
import { RootReducer } from "RootReducer";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCampaignCreate } from "views/admin/campaign_create/roas/components/CampaignCreateContext";
import GamelightButton from "components/button";
import { useStep } from "views/admin/campaign_create/roas/components/StepContext";
import { useQuery } from "hooks/useQuery";
import { FetchOverviewReport } from "views/admin/default/action";
import moment from "moment";
import { Post, Put } from "api";
import {
  hideGlobalLoading,
  showGlobalLoading,
} from "helper/GlobalLoadingDispatches";

const baseSchema = z.object({
  name: z.string().min(1, "Campaign name is required"),
  countries: z.array(z.any()).optional().nullable(),
  genders: z.array(z.any()).optional().nullable(),
  minAge: z.string().optional().nullable(),
  maxAge: z.string().optional().nullable(),
  biddingType: z.enum(["roas_cpi"]).optional().nullable(),
  dailyBudgetCap: z.string().optional().nullable(),
  mmp: z.string().min(1, "MMP is required"),
  clickUrl: z.string().url("Invalid click tracking URL"),
});

const campaignSchema = z
  .discriminatedUnion("platform", [
    z.object({
      platform: z.literal("1"),
      playstoreUrl: z.string().url("Invalid Play Store URL"),
      minApiLevel: z.string().optional().nullable(),
      maxApiLevel: z.string().optional().nullable(),
      appStoreUrl: z.undefined().nullable(),
    }),
    z.object({
      platform: z.literal("2"),
      appStoreUrl: z.string().url("Invalid App Store URL"),
      minApiLevel: z.undefined(),
      maxApiLevel: z.undefined(),
      playstoreUrl: z.string().url("Invalid Play Store URL"),
    }),
  ])
  .and(baseSchema);

type CampaignFormData = z.infer<typeof campaignSchema>;

const mmpOptions = [
  {
    label: "AppsFlyer",
    value: "appsflyer",
  },
  {
    label: "Adjust",
    value: "adjust",
  },
  {
    label: "Singular",
    value: "singular",
  },
  {
    label: "Tenjin",
    value: "tenjin",
  },
];

const platformOptions = [
  { label: "Android", value: "1" },
  { label: "iOS", value: "2" },
];

const determineMMP = (clickUrl: string = "") => {
  if (clickUrl.includes("adjust")) {
    return "adjust";
  } else if (clickUrl.includes(".sng.link")) {
    return "singular";
  } else if (clickUrl.includes("appsflyer")) {
    return "appsflyer";
  } else if (clickUrl.includes("tenjin")) {
    return "tenjin";
  } else {
    return "";
  }
};

const CampaignDetails = () => {
  const toast = useToast();
  const {
    handleUpdateCampaignConfiguration,
    handleUpdateCreatives,
    handleUpdateRoasGoals,
    handleReset,
  } = useCampaignCreate();
  const { nextStep, markStepCompleted, markStepUncompleted } = useStep();

  const searchParams = useQuery();
  const campaignId = searchParams.get("id");

  const isCampaignCreate = !campaignId;

  const [isDirect, setIsDirect] = useState(true);
  const canEditCampaign = isCampaignCreate ? true : isDirect;
  const [isSubmitting, setSubmitting] = useState(false);

  const dispatch = useDispatch();
  const {
    control,
    handleSubmit,
    watch,
    register,
    formState,
    setValue,
    setError,
    clearErrors,
  } = useForm<CampaignFormData>({
    resolver: zodResolver(campaignSchema),
    defaultValues: {
      platform: "1",
      name: "",
      countries: [],
      biddingType: "roas_cpi",
      dailyBudgetCap: "",
      clickUrl: "",
      playstoreUrl: "",
      appStoreUrl: undefined,
      mmp: "",
    },
  });

  const clickTrackingURL = watch("clickUrl");

  const requestState = useSelector(
    (state: RootReducer) => state.overview.fetchOverviewRequestState
  );

  const countryOptions = useSelector(
    (state: RootReducer) => state.campaignDetails.countryOptions
  );

  const showPlatformSelect = localStorage.getItem("p") === "1";
  const accountInfo = useSelector(
    (state: RootReducer) => state.authentication.accountInfo
  );
  const report = useSelector((state: RootReducer) => state.overview.report);
  const campaigns = report ? report.campaigns : [];

  let showPlatformSelection = false;
  if (
    (accountInfo && accountInfo.showPlatformSelection) ||
    showPlatformSelect
  ) {
    showPlatformSelection = true;
  }

  useEffect(() => {
    dispatch(FetchCountryOptions());
    dispatch(FetchOverviewReport(moment().toDate(), moment().toDate(), "all"));
  }, [dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useLayoutEffect(() => {
    handleReset();
  }, [handleReset]);

  useEffect(() => {
    if (!requestState.isSuccessful) return;

    const foundCampaign = campaigns.find((c) => c.id === campaignId);

    if (foundCampaign) {
      const mmp = determineMMP(foundCampaign.clickUrl);

      handleUpdateCampaignConfiguration({
        bid: foundCampaign.bid,
        biddingType: "roas_cpi",
        clickUrl: foundCampaign.clickUrl,
        countries: foundCampaign.countries,
        genders: foundCampaign.genders,
        dailyBudgetCap: foundCampaign.dailyBudgetCap,
        playstoreUrl: foundCampaign.playStoreUrl,
        appStoreUrl: foundCampaign.appStoreUrl,
        iconUrl: foundCampaign.iconUrl,
        mmp: mmp as "appsflyer" | "singular" | "adjust" | "tenjin",
        id: foundCampaign.id,
        name: foundCampaign.name,
        platform: foundCampaign.platform
          ? foundCampaign.platform === "android"
            ? "1"
            : "2"
          : "1",
      });

      setValue("name", foundCampaign.name);
      setValue("platform", foundCampaign.platform === "android" ? "1" : "2");
      setValue("playstoreUrl", foundCampaign.playStoreUrl);
      setValue("appStoreUrl", foundCampaign.appStoreUrl);
      setValue(
        "countries",
        foundCampaign.countries?.map((c) =>
          countryOptions.find((co) => co.value === c)
        ) ?? []
      );
      setValue("biddingType", "roas_cpi");
      setValue(
        "dailyBudgetCap",
        foundCampaign.dailyBudgetCap?.toString() ?? ""
      );
      setValue("mmp", mmp);
      setValue("clickUrl", foundCampaign.clickUrl);

      const _roasGoals = foundCampaign?.roas_goals;
      const transformedRoasGoals: { [key: string]: string } = {};

      _roasGoals?.forEach((r) => {
        transformedRoasGoals[r.dx] =
          r.url === 0 ? "" : Math.floor(r.url * 100).toString();
      });

      handleUpdateRoasGoals(transformedRoasGoals);

      handleUpdateCreatives({
        bannerImageUrl: foundCampaign.creatives?.find(
          (c) => c.type === "banner_image"
        )?.url,
        bannerVideoUrl: foundCampaign.creatives?.find(
          (c) => c.type === "banner_video"
        )?.url,
        iconUrl: foundCampaign.iconUrl,
      });

      handleUpdateCampaignConfiguration({
        title: foundCampaign.appName,
      });

      markStepCompleted(0);
      markStepCompleted(1);
      markStepCompleted(2);
      markStepCompleted(3);

      setIsDirect(foundCampaign.isDirect);
    }
  }, [
    campaigns,
    campaignId,
    handleUpdateCampaignConfiguration,
    setIsDirect,
    requestState.isSuccessful,
  ]);

  useEffect(() => {
    if (clickTrackingURL) {
      const mmp = determineMMP(clickTrackingURL);
      setValue("mmp", mmp);
    }
  }, [clickTrackingURL, setValue]);

  const handleValidateStoreURL = useCallback(
    async (url: string) => {
      if (!url) return;

      try {
        const response = await Post("/dashboard/validate/app", {
          playStoreUrl: url,
        });

        const { bannerImageUrl, bannerVideoUrl, iconUrl, title } = response;

        if (response?.err || !response?.iconUrl) {
          toast({
            title: "Error",
            description: response?.err ?? "An error occurred.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          setError("playstoreUrl", {
            message: response.err,
          });

          handleUpdateCreatives({
            iconUrl: null,
            bannerImageUrl: null,
            bannerVideoUrl: null,
          });

          handleUpdateCampaignConfiguration({
            title: null,
          });

          markStepUncompleted(0);

          return;
        }

        handleUpdateCreatives({
          iconUrl,
          bannerImageUrl,
          bannerVideoUrl,
        });

        handleUpdateCampaignConfiguration({
          title,
        });

        clearErrors("playstoreUrl");
        return true;
      } catch (error) {
        console.error(error);
        toast({
          title: "Error",
          description: "An error occurred while validating the store URL.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    },
    [toast, handleUpdateCampaignConfiguration]
  );

  const handleValidateClickTrackingURL = useCallback(
    async ({
      storeURL,
      clickTrackingURL,
      platformId,
      mmp,
    }: {
      storeURL: string;
      clickTrackingURL: string;
      platformId: number;
      mmp: "appsflyer" | "singular" | "adjust" | "tenjin";
    }) => {
      if (!storeURL) return;

      try {
        const params = new URLSearchParams(new URL(storeURL).search);
        const storeId = params.get("id");

        const response = await Post("/dashboard/link-builder/validate", {
          storeId: storeId,
          trackingUrl: clickTrackingURL,
          platformId: platformId,
          mmp: mmp,
        });
        if (response.error) {
          let message = "";
          if (response.error.endsWith(".")) {
            message = `${response.error} In case you need further assistance, please consult with your AM.`;
          } else {
            message = `${response.error}. In case you need further assistance, please consult with your AM.`;
          }
          toast({
            title: "Error",
            description: message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });

          return;
        }

        clearErrors("clickUrl");
        return true;
      } catch (error) {
        console.error(error);
        toast({
          title: "Error",
          description:
            "An error occurred while validating the Click Tracking URL.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    },
    [toast]
  );

  const onSubmit = useCallback(
    async (data: CampaignFormData) => {
      showGlobalLoading("URL Validation in progress");
      const validateStoreURLRes = await handleValidateStoreURL(
        data.playstoreUrl
      );

      const clickTrackingURLRes = await handleValidateClickTrackingURL({
        storeURL: data.playstoreUrl,
        clickTrackingURL: data.clickUrl,
        platformId: Number(data.platform),
        mmp: data.mmp as "appsflyer" | "singular" | "adjust" | "tenjin",
      });

      if (!validateStoreURLRes || !clickTrackingURLRes) {
        hideGlobalLoading();
        return;
      }

      const _data = {
        name: data.name,
        playstoreUrl: data.playstoreUrl ?? "",
        biddingType: data.biddingType,
        appStoreUrl: data.appStoreUrl ?? "",
        dailyBudgetCap: data.dailyBudgetCap
          ? Number(data.dailyBudgetCap)
          : null,
        countries: data.countries?.map((c) => c.value),
        platform: data.platform,
        mmp:
          (data.mmp as "appsflyer" | "singular" | "adjust" | "tenjin") ?? null,
        clickUrl: data.clickUrl,
      };

      try {
        setSubmitting(true);

        if (campaignId) {
          showGlobalLoading("Updating campaign");
          const response = await Put(`/dashboard/campaigns/${campaignId}`, {
            ..._data,
            platform: _data.platform === "1" ? "android" : "ios",
          });

          if (response.err) {
            toast({
              title: "Error",
              description: response.err,
              status: "error",
              duration: 5000,
              isClosable: true,
            });
            return;
          }

          toast({
            title: "Campaign updated successfully.",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        }

        handleUpdateCampaignConfiguration(
          campaignId ? { ..._data, id: campaignId } : { ..._data }
        );
        nextStep(true);
      } catch (error) {
        console.error(error);
      } finally {
        setSubmitting(false);
        hideGlobalLoading();
      }
    },
    [dispatch, campaignId, nextStep, toast]
  );

  return (
    <Flex
      flexDir="column"
      alignItems={"center"}
      justifyContent="center"
      flex={1}
      h={"90%"}
      gap={8}
      mb="12vh"
    >
      {requestState.isSuccessful ? (
        <SimpleGrid
          columns={1}
          spacing={4}
          w={{
            sm: "100%",
            "2sm": "100%",
            md: "100%",
            lg: "100%",
            xl: "90%",
            "2xl": "80%",
            "3xl": "80%",
          }}
          px={16}
          py={8}
          borderRadius="md"
          bgColor="white"
        >
          <SimpleGrid columns={2} spacing={4} w={"full"}>
            <FormControl isRequired>
              <FormLabel>Campaign Name</FormLabel>
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Input
                    bgColor={"white"}
                    {...field}
                    borderColor={formState.errors.name ? "red.500" : "gray.200"}
                    placeholder="US_EN_Test"
                  />
                )}
              />
            </FormControl>
            {showPlatformSelection ? (
              <FormControl
                id="platform"
                isRequired
                isDisabled={!isCampaignCreate || !canEditCampaign}
              >
                <FormLabel>Platform</FormLabel>
                <ChakraSelect
                  placeholder="Select Platform"
                  bgColor={"white"}
                  {...register("platform")}
                >
                  {platformOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </ChakraSelect>
              </FormControl>
            ) : (
              <></>
            )}
            {watch("platform") === "2" && (
              <FormControl
                isRequired
                isDisabled={!canEditCampaign || !!campaignId}
              >
                <FormLabel>App Store URL</FormLabel>
                <Controller
                  name="appStoreUrl"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      borderColor={
                        formState.errors.appStoreUrl ? "red.500" : "gray.200"
                      }
                      placeholder="https://apps.apple.com/us/app/id123456789"
                    />
                  )}
                />
              </FormControl>
            )}
            <FormControl
              isRequired
              isDisabled={!canEditCampaign || !!campaignId}
            >
              <FormLabel>
                {watch("platform") === "2"
                  ? "Google PlayStore URL (required for the setup)"
                  : "Google PlayStore URL"}
              </FormLabel>
              <Controller
                name="playstoreUrl"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Input
                    bgColor={"white"}
                    {...field}
                    borderColor={
                      formState.errors.playstoreUrl ? "red.500" : "gray.200"
                    }
                    placeholder="https://play.google.com/store/apps/details?id=com.example.app"
                  />
                )}
              />
            </FormControl>
          </SimpleGrid>
          <Box w={"full"} h="1px" bgColor={"gray.200"} borderRadius="md" />
          <SimpleGrid columns={2} spacing={4} w={"full"}>
            <FormControl isDisabled={!canEditCampaign}>
              <FormLabel>Daily Budget Cap</FormLabel>
              <InputGroup>
                <InputLeftElement
                  color="grey.300"
                  fontSize="1.0em"
                  children="$"
                />
                <Input
                  placeholder={"Enter amount"}
                  type={"number"}
                  {...register("dailyBudgetCap")}
                  onBlur={(e) => {
                    const value = e.currentTarget.value;
                    if (!value) return;

                    const parsedValue = parseFloat(value);

                    if (isNaN(parsedValue)) {
                      setValue("dailyBudgetCap", "");
                      toast({
                        title: "Error",
                        description: "Please enter a valid number",
                        status: "error",
                        duration: 5000,
                        isClosable: true,
                      });
                      return;
                    }

                    if (parsedValue < 0) {
                      setValue("dailyBudgetCap", "");
                      toast({
                        title: "Error",
                        description: "Please enter a valid number",
                        status: "error",
                        duration: 5000,
                        isClosable: true,
                      });
                      return;
                    }
                  }}
                />
              </InputGroup>
            </FormControl>
            <FormControl
              id="country"
              zIndex={100}
              isDisabled={!canEditCampaign}
            >
              <FormLabel>Country</FormLabel>
              <Controller
                control={control}
                name="countries"
                render={({ field }) => (
                  <Select
                    {...field}
                    // @ts-ignore
                    options={countryOptions}
                    isMulti
                    maxMenuHeight={400}
                    chakraStyles={{
                      menuList: (props) => ({
                        ...props,
                        maxHeight: "20vh",
                      }),
                      menu: (props) => ({
                        ...props,
                        maxHeight: "20vh",
                      }),
                    }}
                    selectedOptionColor="brand.500"
                    placeholder="Select countries"
                  />
                )}
              />
            </FormControl>
          </SimpleGrid>
          <SimpleGrid columns={2} spacing={4} w={"full"}>
            <FormControl isRequired isDisabled={!canEditCampaign}>
              <FormLabel>Click Tracking URL</FormLabel>
              <Input
                {...register("clickUrl")}
                placeholder="https://example.sng.link/Danx5/y3pt?andi={andi}&aifa={aifa}&ve={os}..."
                borderColor={formState.errors.clickUrl ? "red.500" : "gray.200"}
              />
            </FormControl>
            <FormControl
              isInvalid={watch("mmp") && watch("mmp") === "other"}
              isDisabled={!canEditCampaign}
              isRequired
            >
              <FormLabel>MMP</FormLabel>
              <ChakraSelect
                placeholder="Select an MMP"
                {...register("mmp")}
                borderColor={formState.errors.mmp ? "red.500" : "gray.200"}
              >
                {mmpOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </ChakraSelect>
              <FormErrorMessage style={{ marginBottom: 10 }}>
                Please write us a mail to partners@gamelight.io and we help you
                with the setup within a few hours.
              </FormErrorMessage>
            </FormControl>
          </SimpleGrid>
          <Box w={"full"} h="1px" bgColor={"gray.200"} borderRadius="md" />
          {/*<Accordion borderBottomColor={'transparent'} borderWidth={0} allowToggle>*/}
          {/*  <AccordionItem borderWidth={0}>*/}
          {/*    <AccordionButton borderWidth={0}>*/}
          {/*      <Box as="span" flex="1" textAlign="left" fontWeight={"bold"}>*/}
          {/*        Manual Targeting Options*/}
          {/*      </Box>*/}
          {/*      <AccordionIcon />*/}
          {/*    </AccordionButton>*/}
          {/*    <AccordionPanel pb={4} borderWidth={0}>*/}
          {/*      <SimpleGrid columns={2} spacing={4} w={"full"}>*/}
          {/*        <SimpleGrid columns={2} spacing={4}>*/}
          {/*          <FormControl isDisabled={!canSelectMinMaxApiLevel}>*/}
          {/*            <FormLabel>Min OS</FormLabel>*/}
          {/*            <ChakraSelect*/}
          {/*              textColor={watch("minApiLevel") ? "inherit" : "gray.500"}*/}
          {/*              placeholder="All"*/}
          {/*              {...register("minApiLevel")}*/}
          {/*            >*/}
          {/*              {osOptions.map((option) => (*/}
          {/*                <option key={option.value} value={option.value}>*/}
          {/*                  {option.label}*/}
          {/*                </option>*/}
          {/*              ))}*/}
          {/*            </ChakraSelect>*/}
          {/*          </FormControl>*/}
          {/*          <FormControl isDisabled={!canSelectMinMaxApiLevel}>*/}
          {/*            <FormLabel>Max OS</FormLabel>*/}
          {/*            <ChakraSelect*/}
          {/*              textColor={watch("maxApiLevel") ? "inherit" : "gray.500"}*/}
          {/*              placeholder="All"*/}
          {/*              {...register("maxApiLevel")}*/}
          {/*            >*/}
          {/*              {osOptions.map((option) => (*/}
          {/*                <option key={option.value} value={option.value}>*/}
          {/*                  {option.label}*/}
          {/*                </option>*/}
          {/*              ))}*/}
          {/*            </ChakraSelect>*/}
          {/*          </FormControl>*/}
          {/*        </SimpleGrid>*/}
          {/*        <FormControl zIndex={99} isDisabled={!canEditCampaign}>*/}
          {/*          <FormLabel>Gender</FormLabel>*/}
          {/*          <Controller*/}
          {/*            control={control}*/}
          {/*            name="genders"*/}
          {/*            render={({ field }) => (*/}
          {/*              <Select*/}
          {/*                {...field}*/}
          {/*                // @ts-ignore*/}
          {/*                options={genderOptions}*/}
          {/*                isMulti*/}
          {/*                maxMenuHeight={600}*/}
          {/*                selectedOptionColor="brand.500"*/}
          {/*                placeholder="All"*/}
          {/*              />*/}
          {/*            )}*/}
          {/*          />*/}
          {/*        </FormControl>*/}
          {/*        <SimpleGrid columns={2} spacing={4}>*/}
          {/*          <FormControl isDisabled={!canEditCampaign}>*/}
          {/*            <FormLabel>Min Age</FormLabel>*/}
          {/*            <Input*/}
          {/*              type={"number"}*/}
          {/*              min={12}*/}
          {/*              max={99}*/}
          {/*              placeholder="All"*/}
          {/*              {...register("minAge")}*/}
          {/*            />*/}
          {/*          </FormControl>*/}
          {/*          <FormControl isDisabled={!canEditCampaign}>*/}
          {/*            <FormLabel>Max Age</FormLabel>*/}
          {/*            <Input*/}
          {/*              type={"number"}*/}
          {/*              min={12}*/}
          {/*              max={99}*/}
          {/*              placeholder="All"*/}
          {/*              {...register("maxAge")}*/}
          {/*            />*/}
          {/*          </FormControl>*/}
          {/*        </SimpleGrid>*/}
          {/*      </SimpleGrid>*/}
          {/*    </AccordionPanel>*/}
          {/*  </AccordionItem>*/}
          {/*</Accordion>*/}
          <GamelightButton
            isDisabled={!formState?.isValid || isSubmitting}
            onClick={handleSubmit(onSubmit)}
            isLoading={isSubmitting}
            loadingText={"Processing"}
          >
            Save Campaign Configuration
          </GamelightButton>
        </SimpleGrid>
      ) : (
        <Skeleton
          w={{
            sm: "100%",
            "2sm": "100%",
            md: "100%",
            lg: "100%",
            xl: "90%",
            "2xl": "80%",
            "3xl": "80%",
          }}
          h={400}
        />
      )}
    </Flex>
  );
};

export default CampaignDetails;
